import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixRestaurantsCatalogsV3Discount from './restaurants-catalogs-v3-discount-discount.http.js';

/**
 * Customers can use a discount to pay less. Can be an amount or a percentage.
 * You can read more about discounts in the [introduction](https://dev.wix.com/api/rest/wix-restaurants/catalogs/introduction).
 */
export interface Discount
  extends DiscountValueOneOf,
    DiscountApplyToFilterOneOf {
  /** Discount amount. */
  amount?: Money;
  /**
   * Discount percentage.
   * @decimalValue options { maxScale:3 }
   */
  percentage?: string | null;
  /** IDs of the sections the discount applies to. */
  sectionIds?: SectionIds;
  /** IDs of the items the discount applies to. **Note:** The items must be of type `dish`. */
  itemIds?: ItemIds;
  /**
   * Discount ID.
   * @readonly
   */
  discountId?: string | null;
  /** Discount name. */
  name?: string | null;
  /** Discount description. */
  description?: string | null;
  /** Whether the discount is active. Defaults to `true`. */
  active?: boolean | null;
  /** Discount type. */
  type?: DiscountTypeWithLiterals;
  /**
   * Discount condition.
   * All conditions must be met so that a customer can apply the discount.
   */
  condition?: DiscountCondition;
}

/** @oneof */
export interface DiscountValueOneOf {
  /** Discount amount. */
  amount?: Money;
  /**
   * Discount percentage.
   * @decimalValue options { maxScale:3 }
   */
  percentage?: string | null;
}

/** @oneof */
export interface DiscountApplyToFilterOneOf {
  /** IDs of the sections the discount applies to. */
  sectionIds?: SectionIds;
  /** IDs of the items the discount applies to. **Note:** The items must be of type `dish`. */
  itemIds?: ItemIds;
}

export enum DiscountType {
  UNSPECIFIED_TYPE = 'UNSPECIFIED_TYPE',
  OFF_ITEM = 'OFF_ITEM',
  OFF_ORDER = 'OFF_ORDER',
}

/** @enumType */
export type DiscountTypeWithLiterals =
  | DiscountType
  | 'UNSPECIFIED_TYPE'
  | 'OFF_ITEM'
  | 'OFF_ORDER';

/**
 * Money.
 * Default format to use. Sufficiently compliant with majority of standards: w3c, ISO 4217, ISO 20022, ISO 8583:2003.
 */
export interface Money {
  /**
   * Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.
   * @format DECIMAL_VALUE
   */
  value?: string;
  /**
   * Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   * @format CURRENCY
   * @readonly
   */
  currency?: string;
}

export interface SectionIds {
  values?: string[];
}

export interface ItemIds {
  values?: string[];
}

export interface DiscountCondition {
  /**
   * Which fulfillment types the discount applies to.
   * @minSize 1
   * @maxSize 2
   */
  fulfillmentTypes?: FulfillmentTypeWithLiterals[];
  /**
   * Which ordering platforms the discount applies to.
   * @minSize 1
   * @maxSize 3
   */
  platforms?: DiscountPlatformWithLiterals[];
  /** List of times when the discount is available. */
  availability?: Availability;
  /** Minimum order price for the discount. */
  minOrderPrice?: Money;
  /**
   * Coupon associated with the discount.
   * @readonly
   */
  coupon?: Coupon;
}

export enum FulfillmentType {
  UNSPECIFIED_FULFILLMENT_TYPE = 'UNSPECIFIED_FULFILLMENT_TYPE',
  DELIVERY = 'DELIVERY',
  PICKUP_OR_DINE_IN = 'PICKUP_OR_DINE_IN',
}

/** @enumType */
export type FulfillmentTypeWithLiterals =
  | FulfillmentType
  | 'UNSPECIFIED_FULFILLMENT_TYPE'
  | 'DELIVERY'
  | 'PICKUP_OR_DINE_IN';

export enum DiscountPlatform {
  UNSPECIFIED_PLATFORM = 'UNSPECIFIED_PLATFORM',
  SITE = 'SITE',
  MOBILE_SITE = 'MOBILE_SITE',
  CALL_CENTER = 'CALL_CENTER',
}

/** @enumType */
export type DiscountPlatformWithLiterals =
  | DiscountPlatform
  | 'UNSPECIFIED_PLATFORM'
  | 'SITE'
  | 'MOBILE_SITE'
  | 'CALL_CENTER';

export interface Availability {
  /**
   * Weekly recurring time periods when the entity is available.
   * Limited to 100 time periods.
   */
  periods?: TimePeriod[];
  /** Exceptions to the entity's regular availability. The entity can be available or not available during the special hour period. */
  specialHourPeriods?: SpecialHourPeriod[];
}

/** Weekly recurring time periods when the entity is available. */
export interface TimePeriod {
  /** Day of the week the period starts on. */
  openDay?: DayOfWeekWithLiterals;
  /**
   * Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents
   * midnight at the end of the specified day.
   */
  openTime?: string;
  /** Day of the week the period ends on. */
  closeDay?: DayOfWeekWithLiterals;
  /**
   * Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents
   * midnight at the end of the specified day.
   * __Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.
   */
  closeTime?: string;
}

export enum DayOfWeek {
  UNDEFINED = 'UNDEFINED',
  SUN = 'SUN',
  MON = 'MON',
  TUE = 'TUE',
  WED = 'WED',
  THU = 'THU',
  FRI = 'FRI',
  SAT = 'SAT',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'UNDEFINED'
  | 'SUN'
  | 'MON'
  | 'TUE'
  | 'WED'
  | 'THU'
  | 'FRI'
  | 'SAT';

/** Exception to the business's regular hours. The business can be open or closed during the exception. */
export interface SpecialHourPeriod {
  /** Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  startDate?: string;
  /** End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  endDate?: string;
  /** Whether the item is available during the exception. Defaults to `true`. */
  available?: boolean;
  /** Name of the special hour period. In the dashboard, the special hour period is called event. */
  eventName?: string | null;
}

export interface Coupon {
  /**
   * Whether the customer needs to enter the coupon code to receive the discount. Defaults to `true`.
   * @readonly
   */
  applied?: boolean | null;
  /**
   * Coupon code.
   * @readonly
   */
  code?: string | null;
}

export interface GetDiscountRequest {
  /**
   * ID of the discount to retrieve.
   * @format GUID
   */
  discountId: string;
  /**
   * ID of the catalog the discount belongs to.
   * @format GUID
   */
  catalogId: string;
}

export interface GetDiscountResponse {
  /** Retrieved discount. */
  discount?: Discount;
}

export interface UpdateDiscountRequest {
  /** Discount to update. */
  discount?: Discount;
  /**
   * ID of the catalog the discount belongs to.
   * @format GUID
   */
  catalogId: string;
}

export interface UpdateDiscountResponse {
  /** Updated discount. */
  discount?: Discount;
}

export interface CreateDiscountRequest {
  /** Discount to create. */
  discount?: Discount;
  /**
   * ID of the catalog the discount belongs to.
   * @format GUID
   */
  catalogId: string;
}

export interface CreateDiscountResponse {
  /** Created discount. */
  discount?: Discount;
}

export interface ListDiscountsRequest {
  /** Filed mask path. */
  fieldMask?: string[];
  /** Whether only active discounts are returned. Defaults to `true`. */
  active?: boolean | null;
  /**
   * ID of the catalog the discounts belong to.
   * @format GUID
   */
  catalogId: string;
}

export interface ListDiscountsResponse {
  /** Retrieved discounts. */
  discounts?: Discount[];
}

export interface AddLoyaltyDiscount {
  /** @format GUID */
  locationId?: string | null;
}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface DiscountCreatedEnvelope {
  entity: Discount;
  metadata: EventMetadata;
}

/** @webhook
 * @eventType wix.restaurants.catalogs.v3.discount_created
 * @serviceIdentifier com.wixpress.restaurants.catalogs.v3.DiscountsApi
 * @slug created
 * @documentationMaturity preview
 */
export declare function onDiscountCreated(
  handler: (event: DiscountCreatedEnvelope) => void | Promise<void>
): void;

export interface DiscountUpdatedEnvelope {
  entity: Discount;
  metadata: EventMetadata;
}

/** @webhook
 * @eventType wix.restaurants.catalogs.v3.discount_updated
 * @serviceIdentifier com.wixpress.restaurants.catalogs.v3.DiscountsApi
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onDiscountUpdated(
  handler: (event: DiscountUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves a discount.
 * @public
 * @documentationMaturity preview
 * @requiredField identifiers
 * @requiredField identifiers.catalogId
 * @requiredField identifiers.discountId
 * @permissionId WIX_RESTAURANTS.READ_CATALOGS
 * @applicableIdentity APP
 * @returns Retrieved discount.
 * @fqn com.wixpress.restaurants.catalogs.v3.DiscountsApi.GetDiscount
 * @deprecated
 * @replacedBy com.wix.ecom.discounts.DiscountRuleService.GetDiscountRule
 * @targetRemovalDate 2025-09-30
 */
export async function getDiscount(
  identifiers: NonNullablePaths<
    GetDiscountIdentifiers,
    `catalogId` | `discountId`,
    2
  >
): Promise<
  NonNullablePaths<
    Discount,
    | `amount.value`
    | `amount.currency`
    | `sectionIds.values`
    | `itemIds.values`
    | `type`
    | `condition.fulfillmentTypes`
    | `condition.platforms`
    | `condition.availability.periods`
    | `condition.availability.periods.${number}.openDay`
    | `condition.availability.periods.${number}.openTime`
    | `condition.availability.periods.${number}.closeDay`
    | `condition.availability.periods.${number}.closeTime`
    | `condition.availability.specialHourPeriods`
    | `condition.availability.specialHourPeriods.${number}.startDate`
    | `condition.availability.specialHourPeriods.${number}.endDate`
    | `condition.availability.specialHourPeriods.${number}.available`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    catalogId: identifiers?.catalogId,
    discountId: identifiers?.discountId,
  });

  const reqOpts =
    ambassadorWixRestaurantsCatalogsV3Discount.getDiscount(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.discount!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          catalogId: '$[0].catalogId',
          discountId: '$[0].discountId',
        },
        singleArgumentUnchanged: false,
      },
      ['identifiers']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetDiscountIdentifiers {
  /**
   * ID of the catalog the discount belongs to.
   * @format GUID
   */
  catalogId: string;
  /**
   * ID of the discount to retrieve.
   * @format GUID
   */
  discountId: string;
}

/**
 * Updates a discount.
 * A discount can belong to a catalog, section, or dish. You can't create a discount that applies to a menu.
 * @public
 * @documentationMaturity preview
 * @requiredField identifiers
 * @requiredField identifiers.catalogId
 * @requiredField options
 * @requiredField options.discount
 * @permissionId WIX_RESTAURANTS.MANAGE_CATALOGS
 * @applicableIdentity APP
 * @returns Updated discount.
 * @fqn com.wixpress.restaurants.catalogs.v3.DiscountsApi.UpdateDiscount
 * @deprecated
 * @replacedBy com.wix.ecom.discounts.DiscountRuleService.UpdateDiscountRule
 * @targetRemovalDate 2025-09-30
 */
export async function updateDiscount(
  identifiers: NonNullablePaths<UpdateDiscountIdentifiers, `catalogId`, 2>,
  options: NonNullablePaths<UpdateDiscountOptions, `discount`, 2>
): Promise<
  NonNullablePaths<
    Discount,
    | `amount.value`
    | `amount.currency`
    | `sectionIds.values`
    | `itemIds.values`
    | `type`
    | `condition.fulfillmentTypes`
    | `condition.platforms`
    | `condition.availability.periods`
    | `condition.availability.periods.${number}.openDay`
    | `condition.availability.periods.${number}.openTime`
    | `condition.availability.periods.${number}.closeDay`
    | `condition.availability.periods.${number}.closeTime`
    | `condition.availability.specialHourPeriods`
    | `condition.availability.specialHourPeriods.${number}.startDate`
    | `condition.availability.specialHourPeriods.${number}.endDate`
    | `condition.availability.specialHourPeriods.${number}.available`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    catalogId: identifiers?.catalogId,
    discount: options?.discount,
  });

  const reqOpts =
    ambassadorWixRestaurantsCatalogsV3Discount.updateDiscount(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.discount!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          catalogId: '$[0].catalogId',
          discount: '$[1].discount',
        },
        singleArgumentUnchanged: false,
      },
      ['identifiers', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateDiscountIdentifiers {
  /**
   * ID of the catalog the discount belongs to.
   * @format GUID
   */
  catalogId: string;
}

export interface UpdateDiscountOptions {
  /** Discount to update. */
  discount?: Discount;
}

/**
 * Creates a discount.
 * You can create discounts for catalogs, sections, or dishes. You can't create a discount that applies to a menu.
 * @param catalogId - ID of the catalog the discount belongs to.
 * @public
 * @documentationMaturity preview
 * @requiredField catalogId
 * @requiredField options.discount.active
 * @requiredField options.discount.condition.fulfillmentTypes
 * @requiredField options.discount.condition.minOrderPrice
 * @requiredField options.discount.condition.platforms
 * @requiredField options.discount.name
 * @permissionId WIX_RESTAURANTS.MANAGE_CATALOGS
 * @applicableIdentity APP
 * @returns Created discount.
 * @fqn com.wixpress.restaurants.catalogs.v3.DiscountsApi.CreateDiscount
 * @deprecated
 * @replacedBy com.wix.ecom.discounts.DiscountRuleService.CreateDiscountRule
 * @targetRemovalDate 2025-09-30
 */
export async function createDiscount(
  catalogId: string,
  options?: NonNullablePaths<
    CreateDiscountOptions,
    | `discount.active`
    | `discount.condition.fulfillmentTypes`
    | `discount.condition.minOrderPrice`
    | `discount.condition.platforms`
    | `discount.name`,
    4
  >
): Promise<
  NonNullablePaths<
    Discount,
    | `amount.value`
    | `amount.currency`
    | `sectionIds.values`
    | `itemIds.values`
    | `type`
    | `condition.fulfillmentTypes`
    | `condition.platforms`
    | `condition.availability.periods`
    | `condition.availability.periods.${number}.openDay`
    | `condition.availability.periods.${number}.openTime`
    | `condition.availability.periods.${number}.closeDay`
    | `condition.availability.periods.${number}.closeTime`
    | `condition.availability.specialHourPeriods`
    | `condition.availability.specialHourPeriods.${number}.startDate`
    | `condition.availability.specialHourPeriods.${number}.endDate`
    | `condition.availability.specialHourPeriods.${number}.available`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    catalogId: catalogId,
    discount: options?.discount,
  });

  const reqOpts =
    ambassadorWixRestaurantsCatalogsV3Discount.createDiscount(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.discount!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          catalogId: '$[0]',
          discount: '$[1].discount',
        },
        singleArgumentUnchanged: false,
      },
      ['catalogId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateDiscountOptions {
  /** Discount to create. */
  discount?: Discount;
}

/**
 * Retrieves up to 1000 discounts.
 * @param catalogId - ID of the catalog the discounts belong to.
 * @public
 * @documentationMaturity preview
 * @requiredField catalogId
 * @permissionId WIX_RESTAURANTS.READ_CATALOGS
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.catalogs.v3.DiscountsApi.ListDiscounts
 * @deprecated
 * @replacedBy com.wix.ecom.discounts.DiscountRuleService.QueryDiscountRules
 * @targetRemovalDate 2025-09-30
 */
export async function listDiscounts(
  catalogId: string,
  options?: ListDiscountsOptions
): Promise<
  NonNullablePaths<
    ListDiscountsResponse,
    | `discounts`
    | `discounts.${number}.amount.value`
    | `discounts.${number}.amount.currency`
    | `discounts.${number}.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    catalogId: catalogId,
    fieldMask: options?.fieldMask,
    active: options?.active,
  });

  const reqOpts =
    ambassadorWixRestaurantsCatalogsV3Discount.listDiscounts(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          catalogId: '$[0]',
          fieldMask: '$[1].fieldMask',
          active: '$[1].active',
        },
        singleArgumentUnchanged: false,
      },
      ['catalogId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListDiscountsOptions {
  /** Filed mask path. */
  fieldMask?: string[];
  /** Whether only active discounts are returned. Defaults to `true`. */
  active?: boolean | null;
}
